import { useCurrentAccount } from '@application/api';
import { SeoController, useAuthModal } from '@application/components';
import { useRouter, useShowToast, useValidation } from '@application/utils';
import type { EducatorWriteReviewFormValues } from '@presentation/pages';
import { EducatorWriteReviewPage } from '@presentation/pages';
import type { FormikErrors } from 'formik';
import { useCallback, useEffect, useMemo } from 'react';
import { useWriteReview } from './api/useCreateEducatorReview';
import { useGetEducator } from './api/useGetEducator';

export const EducatorReviewPageController = () => {
  const { show: showAuthModal } = useAuthModal();
  const { currentAccountLoggedIn, currentAccount, currentAccountLoading } = useCurrentAccount();
  const { infoFromCurrentPath, routeLocationDescriptor, translate, navigateToRoute } = useRouter();
  const { showToast } = useShowToast();

  const id = useMemo(() => {
    return infoFromCurrentPath.params[0];
  }, [infoFromCurrentPath.params]);

  const { requiredMessage, containsSensitiveDataMessage, doesNotContainSensitiveData } =
    useValidation();

  const { writeReviewFromFrom } = useWriteReview();

  const { educator, educatorLoading } = useGetEducator(id);

  useEffect(() => {
    if (currentAccountLoading) {
      return;
    }

    if (!currentAccountLoggedIn) {
      showAuthModal();
    }
  }, [currentAccountLoading, currentAccountLoggedIn, showAuthModal]);

  useEffect(() => {
    if (currentAccountLoading || educatorLoading) {
      return;
    }

    const hasAlreadyLeftAReview =
      currentAccount?.id && educator?.reviews?.find((x) => x?.accountId === currentAccount?.id);

    if (currentAccount?.id === educator?.id || hasAlreadyLeftAReview) {
      navigateToRoute('educator', {
        params: [id],
      });
    }
  }, [
    educator?.id,
    educator?.reviews,
    educatorLoading,
    currentAccount?.id,
    currentAccountLoading,
    id,
    navigateToRoute,
  ]);

  const submitHandler = useCallback(
    async (values: EducatorWriteReviewFormValues) => {
      if (!educator) {
        return;
      }

      try {
        await writeReviewFromFrom(values, educator.id);
        showToast(translate('common.toasts.reviewSent'), {
          type: 'success',
        });
        navigateToRoute('educatorList');
      } catch (error) {
        showToast(
          translate('common.toasts.title.error'),
          {
            type: 'error',
          },
          { contactError: `EducatorWriteReview : ${error?.message ?? JSON.stringify(error)}` },
        );
      }
    },
    [educator, navigateToRoute, showToast, translate, writeReviewFromFrom],
  );

  const validate = useCallback(
    (values: EducatorWriteReviewFormValues) => {
      const errors: FormikErrors<EducatorWriteReviewFormValues> = {};
      if (!values.description) {
        errors.description = requiredMessage;
      }
      if (!values.acceptedTerms) {
        errors.acceptedTerms = requiredMessage;
      }
      if (!doesNotContainSensitiveData(values.description)) {
        errors.description = containsSensitiveDataMessage;
      }
      return errors;
    },
    [containsSensitiveDataMessage, doesNotContainSensitiveData, requiredMessage],
  );

  return (
    <>
      <SeoController title={translate('educatorReview.seo.title')} />
      <EducatorWriteReviewPage
        header={{
          title: translate('educatorReview.title'),
          backLink: routeLocationDescriptor('educator', {
            params: [id],
          }),
        }}
        form={{
          initialValues: {
            grade: 5,
            acceptedTerms: false,
            description: '',
          },
          onSubmit: submitHandler,
          validate,
        }}
      />
    </>
  );
};
